import { FileText, Loader2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { useSidebar } from '../../contexts/SidebarContext';
import { BTN_SECONDARY } from '../bureau/buttons';
import { CHIP } from '../bureau/encre';
import { useEncreTone } from '../bureau/encreToneContext';
import { useMeetingReport } from '../../hooks/useMeetingReports';
import type { MeetingReport } from '../../types/meetingReport';

interface MeetingReportBadgeProps {
  noteId?: string | null;
  appointmentId?: string | null;
  /** Variante d'affichage */
  variant?: 'badge' | 'button' | 'inline';
  className?: string;
  /** Pre-loaded report to avoid N+1 API calls in list views */
  preloadedReport?: MeetingReport | null;
}

const FOCUS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus';

/**
 * `legacy` is the notes' and appointments' look, byte for byte. `encre` (an Encre surface,
 * PLAN-ENCRE-OUTILS O4): the report is an accent chip (the blue of what opens something, readable
 * dark), the « Voir le CR » button the ringed pill, the inline link an accent word.
 */
const LOOK = {
  legacy: {
    loading: 'inline-flex items-center gap-1 px-2 py-0.5 text-xs text-neutral-400',
    inline: 'inline-flex items-center gap-1 text-violet-600 hover:text-violet-700 transition-colors',
    button: 'flex items-center gap-2 px-3 py-1.5 bg-violet-50 text-violet-700 rounded-lg text-sm font-medium hover:bg-violet-100 transition-colors',
    badge: 'inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full text-xs font-medium hover:bg-violet-200 transition-colors',
  },
  encre: {
    loading: 'inline-flex items-center gap-1 px-2 py-0.5 text-xs text-desk-muted',
    inline: `inline-flex items-center gap-1 rounded-[4px] text-desk-accent-text transition-colors hover:underline ${FOCUS}`,
    button: BTN_SECONDARY,
    badge: twMerge(CHIP, `gap-1 bg-desk-accent-soft text-desk-accent-text transition-shadow hover:ring-1 hover:ring-inset hover:ring-desk-accent/40 ${FOCUS}`),
  },
} as const;

/**
 * Badge/bouton cliquable qui apparait quand un CR est lié à une note ou un RDV.
 * Un clic ouvre la sidebar MeetingReport.
 */
export function MeetingReportBadge({
  noteId,
  appointmentId,
  variant = 'badge',
  className = '',
  preloadedReport,
}: MeetingReportBadgeProps) {
  const { openMeetingReportSidebar } = useSidebar();
  const look = LOOK[useEncreTone() ? 'encre' : 'legacy'];
  const skipFetch = preloadedReport !== undefined;
  const fetched = useMeetingReport({ noteId, appointmentId, enabled: !skipFetch });
  const report = skipFetch ? preloadedReport : fetched.report;
  const hasReport = skipFetch ? !!preloadedReport : fetched.hasReport;
  const loading = skipFetch ? false : fetched.loading;

  if (loading) {
    if (variant === 'inline') return null;
    return (
      <span className={look.loading}>
        <Loader2 className="w-3 h-3 animate-spin" />
      </span>
    );
  }

  if (!hasReport || !report) return null;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openMeetingReportSidebar(report.id);
  };

  if (variant === 'inline') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`${look.inline} ${className}`}
        title="Voir le compte-rendu"
      >
        <FileText className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">CR</span>
      </button>
    );
  }

  if (variant === 'button') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className={`${look.button} ${className}`}
      >
        <FileText className="w-4 h-4" />
        Voir le CR
      </button>
    );
  }

  // variant === 'badge' (default)
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${look.badge} ${className}`}
      title="Voir le compte-rendu"
    >
      <FileText className="w-3 h-3" />
      CR
    </button>
  );
}
